import {
  ExperienceLevel,
  ProgrammeType,
  DistanceUnit,
  RunningCommunity,
  RunClubDifficulty,
  TrainingIntensityPreference,
} from '../../types/enums';

// ── Step IDs ──────────────────────────────────────────────────────────────────
export type StepId =
  | 'initial_onboarding'
  | 'date_of_birth'
  | 'gender'
  | 'experience_level'
  | 'training_purpose'
  | 'programme_type'
  | 'training_distance'
  | 'race_training_confirmation'
  | 'race_selection'
  | 'race_date'
  | 'training_duration'
  | 'training_start_date'
  | 'goal_time'
  | 'current_pb'
  | 'recent_running_history'
  | 'recent_longest_run_time'
  | 'weekly_training_frequency'
  | 'training_availability'
  | 'long_run_day_preference'
  | 'training_intensity_preference'
  | 'intensive_workout_days_preference'
  | 'distance_unit_preference'
  | 'running_community_participation'
  | 'run_club_day'
  | 'run_club_intensity'
  | 'run_club_distance'
  | 'additional_information'
  | 'summary';

// ── Input Types ───────────────────────────────────────────────────────────────
export type InputType =
  | 'text_input'
  | 'date_picker'
  | 'time_picker'
  | 'value_picker'
  | 'distance_picker'
  | 'multiple_choice'
  | 'multi_select'
  | 'race_search'
  | 'summary';

// ── Option definition ─────────────────────────────────────────────────────────
export interface StepOption {
  label: string;
  value: any;
  description?: string;
}

// ── Step definition ───────────────────────────────────────────────────────────
export interface StepDefinition {
  id: StepId;
  title: string;
  question: string;
  hintText?: string;
  inputType: InputType;
  progress: number;
  canSkip: boolean;
  options?: StepOption[];
  skipOptionText?: string; // text shown below picker as alternative
  valuePickerConfig?: {
    min: number;
    max: number;
    unit: string;
    unitPlural: string;
  };
}

// ── Training Purposes ─────────────────────────────────────────────────────────
export const TRAINING_PURPOSES = [
  'Start my running journey',
  'Train for an upcoming race',
  'Improve my personal best (PB)',
  'Maintain a regular running routine',
  'Run farther than ever',
  'Recover after a recent race',
  'Return to running after an injury',
] as const;

export type TrainingPurpose = (typeof TRAINING_PURPOSES)[number];

// ── Weekdays ──────────────────────────────────────────────────────────────────
export const WEEKDAYS: StepOption[] = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 },
];

// ── All step definitions ──────────────────────────────────────────────────────
export const STEP_DEFINITIONS: Record<StepId, StepDefinition> = {
  initial_onboarding: {
    id: 'initial_onboarding',
    title: 'About you',
    question: "What's your first and last name?",
    inputType: 'text_input',
    progress: 0,
    canSkip: false,
  },
  date_of_birth: {
    id: 'date_of_birth',
    title: 'Personal Information',
    question: "What's your date of birth?",
    hintText: 'We need your date of birth to help us give you the most accurate training plan',
    inputType: 'date_picker',
    progress: 4,
    canSkip: false,
  },
  gender: {
    id: 'gender',
    title: 'Personal Information',
    question: 'What sex were you assigned at birth?',
    inputType: 'multiple_choice',
    progress: 8,
    canSkip: false,
    options: [
      { label: 'Male', value: 'MALE' },
      { label: 'Female', value: 'FEMALE' },
      { label: 'Prefer Not to Say', value: 'PREFER NOT TO SAY' },
    ],
  },
  experience_level: {
    id: 'experience_level',
    title: 'Experience Level',
    question: 'What is your running experience level?',
    inputType: 'multiple_choice',
    progress: 10,
    canSkip: false,
    options: [
      { label: 'Beginner', value: ExperienceLevel.BEGINNER, description: "You're new to running, focused on building fitness and being able to complete a 5km without stopping." },
      { label: 'Novice', value: ExperienceLevel.NOVICE, description: 'Can run 5-10km comfortably, looking to build distance.' },
      { label: 'Intermediate', value: ExperienceLevel.INTERMEDIATE, description: 'Regular runner, completed races, looking for structured training.' },
      { label: 'Advanced', value: ExperienceLevel.ADVANCED, description: 'Experienced competitive runner with specific performance goals.' },
    ],
  },
  training_purpose: {
    id: 'training_purpose',
    title: 'Training Goals',
    question: "What's your current running goal?",
    inputType: 'multiple_choice',
    progress: 12,
    canSkip: false,
    // options built dynamically based on experience level
  },
  programme_type: {
    id: 'programme_type',
    title: 'Programme Type',
    question: 'What distance are you training for?',
    inputType: 'multiple_choice',
    progress: 16,
    canSkip: false,
    options: [
      { label: '5K', value: ProgrammeType.FIVE_K },
      { label: '10K', value: ProgrammeType.TEN_K },
      { label: 'Half Marathon', value: ProgrammeType.HALF_MARATHON },
      { label: 'Marathon', value: ProgrammeType.MARATHON },
      { label: 'Ultra Marathon', value: ProgrammeType.ULTRA_MARATHON },
      { label: 'Something else', value: ProgrammeType.SOMETHING_ELSE },
      { label: 'No specific distance', value: ProgrammeType.NONE },
    ],
  },
  training_distance: {
    id: 'training_distance',
    title: 'Training Distance',
    question: 'What distance are you training for?',
    inputType: 'distance_picker',
    progress: 16,
    canSkip: false,
  },
  race_training_confirmation: {
    id: 'race_training_confirmation',
    title: 'Race Training',
    question: 'Are you training for an upcoming race for this distance?',
    inputType: 'multiple_choice',
    progress: 20,
    canSkip: false,
    options: [
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ],
  },
  race_selection: {
    id: 'race_selection',
    title: 'Race Selection',
    question: 'Enter the name of your race',
    inputType: 'race_search',
    progress: 24,
    canSkip: true,
  },
  race_date: {
    id: 'race_date',
    title: 'Race Date',
    question: "What's the date of the race?",
    inputType: 'date_picker',
    progress: 36,
    canSkip: false,
  },
  training_duration: {
    id: 'training_duration',
    title: 'Training Duration',
    question: 'How many weeks would you like to train for?',
    inputType: 'value_picker',
    progress: 28,
    canSkip: false,
    valuePickerConfig: { min: 1, max: 52, unit: 'week', unitPlural: 'weeks' },
  },
  training_start_date: {
    id: 'training_start_date',
    title: 'Training Start',
    question: 'When do you want to start your training?',
    hintText: 'Choose a start date — this week or any time in the future.',
    inputType: 'date_picker',
    progress: 40,
    canSkip: false,
  },
  goal_time: {
    id: 'goal_time',
    title: 'Goal Time',
    question: "What's your goal time for this distance?",
    inputType: 'time_picker',
    progress: 44,
    canSkip: false,
    skipOptionText: "I'm not sure yet",
  },
  current_pb: {
    id: 'current_pb',
    title: 'Personal Best',
    question: "What's your current personal best (PB) for this distance (if you have one)?",
    inputType: 'time_picker',
    progress: 48,
    canSkip: false,
    skipOptionText: "I don't have a personal best for this distance",
  },
  recent_running_history: {
    id: 'recent_running_history',
    title: 'Running History',
    question: "What's the longest run you've completed in the last 6 weeks?",
    inputType: 'distance_picker',
    progress: 52,
    canSkip: false,
    skipOptionText: "I haven't run in the past 6 weeks",
  },
  recent_longest_run_time: {
    id: 'recent_longest_run_time',
    title: 'Longest Recent Run',
    question: 'How long did that run take you?',
    inputType: 'time_picker',
    progress: 54,
    canSkip: false,
    skipOptionText: "I don't know",
  },
  weekly_training_frequency: {
    id: 'weekly_training_frequency',
    title: 'Training Frequency',
    question: 'How many times would you like to run each week?',
    inputType: 'value_picker',
    progress: 56,
    canSkip: false,
    valuePickerConfig: { min: 1, max: 7, unit: 'run', unitPlural: 'runs per week' },
  },
  training_availability: {
    id: 'training_availability',
    title: 'Training Availability',
    question: "Are there any days you can't train?",
    inputType: 'multi_select',
    progress: 72,
    canSkip: true,
    options: WEEKDAYS,
  },
  long_run_day_preference: {
    id: 'long_run_day_preference',
    title: 'Long Run Schedule',
    question: 'Which day would you prefer to do your long run?',
    inputType: 'multiple_choice',
    progress: 60,
    canSkip: false,
    options: WEEKDAYS,
  },
  training_intensity_preference: {
    id: 'training_intensity_preference',
    title: 'Training Intensity',
    question: 'How intense would you like your training to be?',
    inputType: 'multiple_choice',
    progress: 64,
    canSkip: false,
    options: [
      { label: 'I prefer easy runs (no hard sessions)', value: TrainingIntensityPreference.LOW },
      { label: 'One challenging session per week (intervals or tempo runs)', value: TrainingIntensityPreference.MEDIUM },
      { label: 'Two challenging sessions per week (intervals or tempo runs)', value: TrainingIntensityPreference.HIGH },
    ],
  },
  intensive_workout_days_preference: {
    id: 'intensive_workout_days_preference',
    title: 'Hard Session Schedule',
    question: 'Which day(s) of the week would you prefer for your hard or interval sessions?',
    inputType: 'multi_select',
    progress: 68,
    canSkip: false,
    options: WEEKDAYS,
  },
  distance_unit_preference: {
    id: 'distance_unit_preference',
    title: 'Distance Unit',
    question: 'Do you prefer to run in kilometres or miles?',
    inputType: 'multiple_choice',
    progress: 76,
    canSkip: false,
    options: [
      { label: 'Kilometres', value: DistanceUnit.KM },
      { label: 'Miles', value: DistanceUnit.MILE },
    ],
  },
  running_community_participation: {
    id: 'running_community_participation',
    title: 'Running Community',
    question: 'Do you attend a weekly run club or parkrun?',
    inputType: 'multiple_choice',
    progress: 80,
    canSkip: false,
    options: [
      { label: 'Run Club', value: RunningCommunity.RUN_CLUB },
      { label: 'Parkrun', value: RunningCommunity.PARK_RUN },
      { label: 'No', value: RunningCommunity.NONE },
    ],
  },
  run_club_day: {
    id: 'run_club_day',
    title: 'Run Club Schedule',
    question: 'Which day is your run club?',
    inputType: 'multiple_choice',
    progress: 84,
    canSkip: false,
    options: WEEKDAYS,
  },
  run_club_intensity: {
    id: 'run_club_intensity',
    title: 'Run Club Intensity',
    question: 'Is it usually a hard or easy run?',
    inputType: 'multiple_choice',
    progress: 88,
    canSkip: false,
    options: [
      { label: 'Hard', value: RunClubDifficulty.HARD },
      { label: 'Easy', value: RunClubDifficulty.EASY },
    ],
  },
  run_club_distance: {
    id: 'run_club_distance',
    title: 'Run Club Distance',
    question: 'What distance do you typically run at your club?',
    inputType: 'distance_picker',
    progress: 92,
    canSkip: false,
  },
  additional_information: {
    id: 'additional_information',
    title: 'Additional Information',
    question: 'Anything else we should know to make your plan work better for you?',
    hintText: "E.g. I'm returning to training after a long-standing calf injury; I have a holiday in 6 weeks and won't be able to run then; I don't have time to train on Wednesdays",
    inputType: 'text_input',
    progress: 96,
    canSkip: true,
  },
  summary: {
    id: 'summary',
    title: 'Summary',
    question: 'Review your answers before we create your personalised training plan',
    inputType: 'summary',
    progress: 100,
    canSkip: false,
  },
};

// ── Dynamic options builder ───────────────────────────────────────────────────
export function getTrainingPurposeOptions(experienceLevel: string | null): StepOption[] {
  const all: StepOption[] = [
    { label: 'Start my running journey', value: 'Start my running journey' },
    { label: 'Train for an upcoming race', value: 'Train for an upcoming race' },
    { label: 'Improve my personal best (PB)', value: 'Improve my personal best (PB)' },
    { label: 'Maintain a regular running routine', value: 'Maintain a regular running routine' },
    { label: 'Run farther than ever', value: 'Run farther than ever' },
    { label: 'Recover after a recent race', value: 'Recover after a recent race' },
    { label: 'Return to running after an injury', value: 'Return to running after an injury' },
  ];
  if (experienceLevel && experienceLevel !== ExperienceLevel.BEGINNER) {
    return all.filter((o) => o.value !== 'Start my running journey');
  }
  return all;
}

// ── Duration limits by programme type ─────────────────────────────────────────
export function getDurationLimits(programmeType: string | null): { min: number; max: number; initial: number } {
  switch (programmeType) {
    case ProgrammeType.FIVE_K:
      return { min: 12 * 60, max: 2 * 3600, initial: 30 * 60 };
    case ProgrammeType.TEN_K:
      return { min: 25 * 60, max: 3 * 3600, initial: 60 * 60 };
    case ProgrammeType.HALF_MARATHON:
      return { min: 3600, max: 6 * 3600, initial: 2 * 3600 };
    case ProgrammeType.MARATHON:
      return { min: 2 * 3600, max: 10 * 3600, initial: 4 * 3600 };
    case ProgrammeType.ULTRA_MARATHON:
      return { min: 3 * 3600, max: 72 * 3600, initial: 4 * 3600 };
    default:
      return { min: 0, max: 10 * 3600, initial: 60 * 60 };
  }
}

// ── Distance limits by question + programme ───────────────────────────────────
export function getDistanceLimits(
  stepId: StepId,
  programmeType: string | null
): { minKm: number; maxKm: number; minMiles: number; maxMiles: number } {
  if (stepId === 'training_distance') {
    if (programmeType === ProgrammeType.ULTRA_MARATHON) {
      return { minKm: 43, maxKm: 250, minMiles: 27, maxMiles: 100 };
    }
    if (programmeType === ProgrammeType.SOMETHING_ELSE) {
      return { minKm: 1, maxKm: 42.2, minMiles: 1, maxMiles: 26.2 };
    }
  }
  return { minKm: 1, maxKm: 50, minMiles: 1, maxMiles: 30 };
}

// ── Date limits ───────────────────────────────────────────────────────────────
export function getDateLimits(stepId: StepId): { min?: string; max?: string } {
  const today = new Date();
  if (stepId === 'date_of_birth') {
    const maxDate = new Date(today);
    maxDate.setFullYear(maxDate.getFullYear() - 13);
    const minDate = new Date(today);
    minDate.setFullYear(minDate.getFullYear() - 120);
    return { min: formatDate(minDate), max: formatDate(maxDate) };
  }
  if (stepId === 'race_date') {
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 7);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 7 * 52);
    return { min: formatDate(minDate), max: formatDate(maxDate) };
  }
  if (stepId === 'training_start_date') {
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 180);
    return { min: formatDate(today), max: formatDate(maxDate) };
  }
  return {};
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

// ── Max selection for multi-select ────────────────────────────────────────────
export function getMaxSelection(
  stepId: StepId,
  answers: Record<string, any>
): number {
  if (stepId === 'intensive_workout_days_preference') {
    const intensity = answers.training_intensity_preference;
    if (intensity === TrainingIntensityPreference.HIGH) return 2;
    return 1;
  }
  if (stepId === 'training_availability') {
    const runs = answers.weekly_training_frequency;
    if (typeof runs === 'number') return 7 - runs;
    return 5;
  }
  return 7;
}

// ── Branching logic ───────────────────────────────────────────────────────────
export function getNextStep(
  currentStep: StepId,
  answers: Record<string, any>
): StepId | null {
  const purpose: string | undefined = answers.training_purpose;
  const programme: string | undefined = answers.programme_type;
  const intensity = answers.training_intensity_preference;
  const runsPerWeek: number | undefined = answers.weekly_training_frequency;
  const community: string | undefined = answers.running_community_participation;

  switch (currentStep) {
    case 'initial_onboarding':
      return 'date_of_birth';
    case 'date_of_birth':
      return 'gender';
    case 'gender':
      return 'experience_level';
    case 'experience_level':
      return 'training_purpose';

    case 'training_purpose':
      if (
        purpose === 'Return to running after an injury' ||
        purpose === 'Recover after a recent race'
      ) {
        return 'training_duration';
      }
      return 'programme_type';

    case 'programme_type':
      if (
        programme === ProgrammeType.ULTRA_MARATHON ||
        programme === ProgrammeType.SOMETHING_ELSE
      ) {
        return 'training_distance';
      }
      if (purpose === 'Train for an upcoming race') {
        return 'race_selection';
      }
      if (
        purpose === 'Maintain a regular running routine' ||
        programme === ProgrammeType.NONE
      ) {
        return 'training_duration';
      }
      return 'race_training_confirmation';

    case 'training_distance':
      if (purpose === 'Train for an upcoming race') {
        return 'race_selection';
      }
      if (
        purpose === 'Maintain a regular running routine' ||
        programme === ProgrammeType.NONE
      ) {
        return 'training_duration';
      }
      return 'race_training_confirmation';

    case 'race_training_confirmation':
      if (answers.race_training_confirmation === 'Yes') {
        return 'race_selection';
      }
      return 'training_duration';

    case 'race_selection': {
      const raceEvent = answers.race_selection;
      if (raceEvent && typeof raceEvent === 'object' && !raceEvent.startDate) {
        return 'race_date';
      }
      return 'training_start_date';
    }

    case 'training_duration':
    case 'race_date':
      return 'training_start_date';

    case 'training_start_date':
      if (
        programme === ProgrammeType.NONE ||
        purpose === 'Return to running after an injury' ||
        purpose === 'Recover after a recent race'
      ) {
        return 'recent_running_history';
      }
      return 'goal_time';

    case 'goal_time':
      return 'current_pb';

    case 'current_pb':
      return 'recent_running_history';

    case 'recent_running_history': {
      const goalTime = answers.goal_time;
      const currentPb = answers.current_pb;
      const recentHistory = answers.recent_running_history;
      const isGoalSkipped = typeof goalTime === 'string';
      const isPbSkipped = typeof currentPb === 'string';
      const hasNoGoalOrPb =
        (!goalTime || isGoalSkipped) && (!currentPb || isPbSkipped);
      const hasRecentRun =
        recentHistory && typeof recentHistory === 'object' && recentHistory.distance;
      if (hasNoGoalOrPb && hasRecentRun) {
        return 'recent_longest_run_time';
      }
      return 'weekly_training_frequency';
    }

    case 'recent_longest_run_time':
      return 'weekly_training_frequency';

    case 'weekly_training_frequency':
      if (runsPerWeek === 7) return 'long_run_day_preference';
      if (runsPerWeek === 1) return 'distance_unit_preference';
      return 'training_availability';

    case 'training_availability':
      return 'long_run_day_preference';

    case 'long_run_day_preference':
      return 'training_intensity_preference';

    case 'training_intensity_preference':
      if (intensity === TrainingIntensityPreference.LOW) {
        return 'distance_unit_preference';
      }
      return 'intensive_workout_days_preference';

    case 'intensive_workout_days_preference':
      return 'distance_unit_preference';

    case 'distance_unit_preference':
      return 'running_community_participation';

    case 'running_community_participation':
      if (community === RunningCommunity.RUN_CLUB) return 'run_club_day';
      if (community === RunningCommunity.PARK_RUN) return 'run_club_intensity';
      return 'additional_information';

    case 'run_club_day':
      return 'run_club_intensity';

    case 'run_club_intensity':
      if (community === RunningCommunity.RUN_CLUB) return 'run_club_distance';
      return 'additional_information';

    case 'run_club_distance':
      return 'additional_information';

    case 'additional_information':
      return 'summary';

    case 'summary':
      return null;

    default:
      return null;
  }
}
